import { useState } from 'react';
import { CalendarDays, ChevronDown, ChevronsUpDown, ExternalLink, X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface RefundManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderCode?: string;
}

type PrimaryTab = 'refund_order' | 'magento_refund';
type RefundMode = 'entire' | 'single';

const fieldLabelClass = 'mb-1.5 block text-[13px] font-medium text-[#222A43]';
const fieldInputClass =
  'h-9 w-full rounded-md border border-[#D8DDE7] bg-white px-3 text-[13px] text-[#7B88A9] focus:outline-none focus:ring-1 focus:ring-[#C4CCE0]';

function SortTitle({ title }: { title: string }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <span>{title}</span>
      <ChevronsUpDown className="h-3.5 w-3.5 text-[#93A0BF]" />
    </div>
  );
}

export default function RefundManagementModal({
  isOpen,
  onClose,
  orderCode = '01-1022-21695',
}: RefundManagementModalProps) {
  const [primaryTab, setPrimaryTab] = useState<PrimaryTab>('refund_order');
  const [refundMode, setRefundMode] = useState<RefundMode>('entire');

  if (!isOpen) return null;

  const renderOrderItemsTable = () => (
    <div className="overflow-hidden rounded-[6px] border border-[#DCE0EA] bg-white">
      <div className="flex items-center justify-between border-b border-[#E4E8F0] bg-[#F0F1F6] px-5 py-2.5">
        <h3 className="text-[16px] font-medium text-[#1A2444]">Order Item List</h3>
        <ChevronDown className="h-5 w-5 text-[#1E2330]" />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-[13px]">
          <thead className="border-b border-[#E7EAF2] bg-white text-[#7A86A6]">
            <tr>
              <th className="w-12 px-4 py-3 text-left font-medium">
                <input type="checkbox" className="h-4 w-4 rounded-sm border-[#CAD2E5] accent-[#F5C500]" />
              </th>
              <th className="px-4 py-3 text-left font-medium"><SortTitle title="Item Id" /></th>
              <th className="px-4 py-3 text-left font-medium"><SortTitle title="URL" /></th>
              <th className="px-4 py-3 text-left font-medium"><SortTitle title="Refund Status" /></th>
              <th className="px-4 py-3 text-left font-medium"><SortTitle title="Total Charged Fee" /></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#ECEFF5] text-[#1F2845]">
            {Array.from({ length: 4 }).map((_, index) => (
              <tr key={index} className="hover:bg-[#FAFBFF]">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    defaultChecked={index > 0}
                    className="h-4 w-4 rounded-sm border-[#CAD2E5] accent-[#F5C500]"
                  />
                </td>
                <td className="px-4 py-3 text-[13px] font-medium">01-1022-21695</td>
                <td className="px-4 py-3 text-[13px] font-medium text-[#1F2845]">Open URL</td>
                <td className="px-4 py-3 text-[13px] font-medium text-[#1F2845]">Partial fulfillment</td>
                <td className="px-4 py-3 text-[13px] font-medium text-[#1F2845]">599.75 USD</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-[#E7EAF2] bg-[#F7F7FA] px-5 py-3 text-[13px] font-medium text-[#141B31]">
        MKT Order fees: 2 usd
      </div>
    </div>
  );

  const renderMarketplaceInfo = () => (
    <div className="overflow-hidden rounded-[6px] border border-[#DCE0EA] bg-white">
      <div className="flex items-center justify-between border-b border-[#E4E8F0] bg-[#F0F1F6] px-5 py-2.5">
        <h3 className="text-[16px] font-medium text-[#1A2444]">Marketplace Information</h3>
        <ChevronDown className="h-5 w-5 text-[#1E2330]" />
      </div>

      <div className="p-5">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[170px_minmax(0,1fr)]">
          <div className="flex items-center justify-center pt-4 lg:items-start">
            <div className="relative overflow-hidden rounded-lg border border-[#E0E4ED] bg-white shadow-sm">
              <img src="/img/Image 4.png" alt="Product" className="h-[128px] w-[126px] object-cover" />
              <span className="absolute right-2 top-2 inline-flex h-4 w-4 items-center justify-center rounded bg-white/80 text-[#1F2432]">
                <ExternalLink className="h-3 w-3" />
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Marketplace Zone', value: '2024-07-31 10:25:20' },
              { label: 'Manual Order / Platform', value: '' },
              { label: 'Product Price', value: '13.22' },
              { label: 'Shipping Fee', value: '' },
              { label: 'Taxes', value: '13,22' },
              { label: 'App Fees', value: '1' },
              { label: 'Total', value: '24.79' },
              { label: 'MKT Taxes', value: '' },
              { label: 'MKT Order Fees', value: '' },
              { label: 'MKT Order Fees', value: '' },
              { label: 'MKT Item Delivery', value: '' },
              { label: 'Order Completion', value: '' },
            ].map((field, index) => (
              <div key={`${field.label}-${index}`}>
                <label className={fieldLabelClass}>{field.label}</label>
                <input type="text" readOnly defaultValue={field.value} className={fieldInputClass} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRefundOrderContent = () => {
    if (refundMode === 'single') {
      return (
        <div className="space-y-4 pb-2">
          {renderOrderItemsTable()}

          <div className="overflow-hidden rounded-[6px] border border-[#DCE0EA] bg-white">
            <button className="flex w-full items-center justify-between bg-[#F0F1F6] px-5 py-2.5 text-[16px] font-medium text-[#1A2444]">
              <span>Marketplace Information</span>
              <ChevronDown className="h-5 w-5 text-[#1E2330]" />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4 pb-2">
        {renderMarketplaceInfo()}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="overflow-hidden rounded-[6px] border border-[#DCE0EA] bg-white">
            <button className="flex w-full items-center justify-between border-b border-[#E4E8F0] bg-[#F0F1F6] px-5 py-2.5 text-[16px] font-medium text-[#1A2444]">
              <span>Order Refund {orderCode}</span>
              <ChevronDown className="h-5 w-5 text-[#1E2330]" />
            </button>
            <div className="p-4">
              {[
                { label: 'Payment type', value: 'Paid Amount' },
                { label: 'eWallet', value: '30.00 USD' },
                { label: 'Payment type', value: 'Paid Amount' },
                { label: 'eWallet', value: '30.00 USD' },
              ].map((row, idx) => (
                <div key={idx} className="flex items-center justify-between border-b border-[#ECEFF5] py-3 text-[14px] last:border-b-0">
                  <span className="text-[#6F7A98]">{row.label}</span>
                  <span className="font-semibold text-[#1E2330]">{row.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-[6px] border border-[#DCE0EA] bg-white">
            <button className="flex w-full items-center justify-between border-b border-[#E4E8F0] bg-[#F0F1F6] px-5 py-2.5 text-[16px] font-medium text-[#1A2444]">
              <span>Order Amount</span>
              <ChevronDown className="h-5 w-5 text-[#1E2330]" />
            </button>
            <div className="space-y-3 p-4">
              <div>
                <label className={fieldLabelClass}>Cost</label>
                <input type="text" className={fieldInputClass} defaultValue="Cost" />
              </div>
              <button className="h-7 w-full rounded-md bg-[#F5C500] text-[12px] font-medium text-[#1E2330] hover:bg-[#E5B900]">
                Submit
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-[6px] border border-[#DCE0EA] bg-white">
          <button className="flex w-full items-center justify-between border-b border-[#E4E8F0] bg-[#F0F1F6] px-5 py-2.5 text-[16px] font-medium text-[#1A2444]">
            <span>Order Payment Option</span>
            <ChevronDown className="h-5 w-5 text-[#1E2330]" />
          </button>
          <div className="divide-y divide-[#ECEFF5] px-5 py-2 text-[14px]">
            {[
              { label: 'Total Fees', value: '577. USd' },
              { label: 'Mahalkum Giftcard', value: '0.00 USD' },
              { label: 'eWallet', value: '30.00 USD' },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between py-3">
                <span className="text-[#6F7A98]">{item.label}</span>
                <span className="font-semibold text-[#1E2330]">{item.value}</span>
              </div>
            ))}
            <div className="flex items-center justify-between py-3">
              <span className="text-[14px] font-medium text-[#202944]">Total Amount Without Coupon</span>
              <span className="text-[14px] font-semibold text-[#202944]">599.75 USD</span>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-[6px] border border-[#DCE0EA] bg-white">
          <button className="flex w-full items-center justify-between border-b border-[#E4E8F0] bg-[#F0F1F6] px-5 py-2.5 text-[16px] font-medium text-[#1A2444]">
            <span>Order Completion Payment Info</span>
            <ChevronDown className="h-5 w-5 text-[#1E2330]" />
          </button>
          <div className="divide-y divide-[#ECEFF5] px-5 py-2 text-[14px]">
            <div className="flex items-center justify-between py-3">
              <span className="text-[#6F7A98]">Total Fees</span>
              <span className="font-semibold text-[#1E2330]">0.00 USD</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-[14px] font-medium text-[#202944]">Total Amount Without Coupon</span>
              <span className="text-[14px] font-semibold text-[#202944]">599.75 USD</span>
            </div>
          </div>
        </div>

        {renderOrderItemsTable()}
      </div>
    );
  };

  const renderMagentoRefundContent = () => (
    <div className="space-y-4 pb-2">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={fieldLabelClass}>Amount</label>
          <input type="text" className={fieldInputClass} defaultValue="2024-07-31 10:25:20" />
        </div>
        <div>
          <label className={fieldLabelClass}>Transaction Number</label>
          <input type="text" className={fieldInputClass} defaultValue="" />
        </div>

        <div>
          <label className={fieldLabelClass}>Refund Date</label>
          <div className="relative">
            <input type="text" className={`${fieldInputClass} pr-9`} defaultValue="12-24-2024" />
            <CalendarDays className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8190B2]" />
          </div>
        </div>
        <div>
          <label className={fieldLabelClass}>Refund Assigned By</label>
          <div className="relative">
            <select className={`${fieldInputClass} appearance-none`}>
              <option>Select User</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8190B2]" />
          </div>
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-[13px] text-[#505E82]">
        <input type="checkbox" className="h-4 w-4 rounded-sm border-[#CAD2E5] accent-[#F5C500]" />
        <span>This item is subject to a refunded marketplace fee</span>
      </label>

      <div>
        <label className="mb-1.5 block text-[13px] font-medium text-[#222A43]">Last Mile Delivery</label>
        <textarea
          className="min-h-[86px] w-full rounded-md border border-[#D8DDE7] bg-white px-3 py-2 text-[13px] text-[#4D5D83] focus:outline-none focus:ring-1 focus:ring-[#C4CCE0]"
          defaultValue="Textarea"
        />
      </div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full max-w-[1120px] overflow-hidden bg-[#F4F5F8] shadow-2xl">
        <div className="flex h-full flex-col">
        <div className="flex items-center justify-between bg-[#223A65] px-7 py-8 text-white">
          <h2 className="text-[40px] font-medium leading-none">Refund Management</h2>
          <button
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#4469A4] text-white transition-colors hover:bg-[#35588E]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="bg-[#F4F5F8] px-7 pt-5">
          <div className="flex justify-center gap-8">
            <button
              onClick={() => setPrimaryTab('refund_order')}
              className={`h-10 min-w-[156px] rounded-[8px] px-6 text-[14px] font-medium leading-none ${
                primaryTab === 'refund_order'
                  ? 'bg-[#F5C500] text-[#171C2A]'
                  : 'bg-[#E4E6EC] text-[#8C98B5]'
              }`}
            >
              {primaryTab === 'magento_refund' ? 'Refund Item' : 'Refund Order'}
            </button>
            <button
              onClick={() => setPrimaryTab('magento_refund')}
              className={`h-10 min-w-[156px] rounded-[8px] px-6 text-[14px] font-medium leading-none ${
                primaryTab === 'magento_refund'
                  ? 'bg-[#F5C500] text-[#171C2A]'
                  : 'bg-[#E4E6EC] text-[#8C98B5]'
              }`}
            >
              Magneto Refund
            </button>
          </div>

          {primaryTab === 'refund_order' && (
            <div className="mx-auto mt-4 flex h-11 max-w-[840px] items-center justify-center gap-8 rounded-[4px] border border-[#E4E8F0] bg-white px-4">
              <label
                className={`inline-flex cursor-pointer items-center gap-3 text-[14px] ${
                  refundMode === 'entire' ? 'text-[#9A7B00]' : 'text-[#556286]'
                }`}
              >
                <input
                  type="radio"
                  checked={refundMode === 'entire'}
                  onChange={() => setRefundMode('entire')}
                  className="sr-only"
                />
                <span
                  aria-hidden="true"
                  className="inline-block h-5 w-5 rounded-full border-solid bg-white"
                  style={{
                    borderColor: refundMode === 'entire' ? '#F5C500' : '#E6EAF2',
                    borderWidth: refundMode === 'entire' ? '5px' : '2px',
                  }}
                />
                <span>Refund Entire Order</span>
              </label>
              <label
                className={`inline-flex cursor-pointer items-center gap-3 text-[14px] ${
                  refundMode === 'single' ? 'text-[#9A7B00]' : 'text-[#556286]'
                }`}
              >
                <input
                  type="radio"
                  checked={refundMode === 'single'}
                  onChange={() => setRefundMode('single')}
                  className="sr-only"
                />
                <span
                  aria-hidden="true"
                  className="inline-block h-5 w-5 rounded-full border-solid bg-white"
                  style={{
                    borderColor: refundMode === 'single' ? '#F5C500' : '#E6EAF2',
                    borderWidth: refundMode === 'single' ? '5px' : '2px',
                  }}
                />
                <span>Refund Single Item</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-7 py-4">
          {primaryTab === 'refund_order' ? renderRefundOrderContent() : renderMagentoRefundContent()}
        </div>

        <div className="border-t border-[#DDE2EC] bg-white px-7 py-4">
          {primaryTab === 'refund_order' && refundMode === 'single' ? (
            <div className="flex items-center justify-between">
              <span className="text-[36px] leading-none text-[#26314E]">4 Items selected for refund</span>
              <button className="h-9 min-w-[140px] rounded-[8px] bg-[#F5C500] px-6 text-[14px] font-medium text-[#171C2A] hover:bg-[#E5B900]">
                Submit Now
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-4">
              <button className="h-9 min-w-[86px] rounded-[8px] bg-[#1F2432] px-5 text-[14px] font-medium text-white hover:bg-[#171C29]">
                Receipt
              </button>
              <button className="h-9 min-w-[140px] rounded-[8px] bg-[#F5C500] px-6 text-[14px] font-medium text-[#171C2A] hover:bg-[#E5B900]">
                Submit Now
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
